import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  issueViewerSecondaryViewQueryKey,
  issueViewerSecondaryViewQueryOptions,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This direct options entrypoint has no runtime Relay dependency.
} from '@github-ui/issue-viewer/IssueViewerSecondaryViewQuery'
import type {IssueCoordinates} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {fetchAndWarmPreheat, runSharedPreheat} from '@github-ui/preheat-issues-manager/shared-preheat-run'
import {createQueryClient, getQueryClient} from '@github-ui/query-client'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'

let preheatQueryClient: ReturnType<typeof createQueryClient> | undefined

export async function preheatIssueViewerSecondary({
  owner,
  repo,
  number,
  source,
  sendAnalyticsEvent,
  signal,
  onError,
}: IssueCoordinates & {
  source: string
  sendAnalyticsEvent: SendAnalyticsEventFunction
  signal: AbortSignal
  onError: (error: unknown) => void
}): Promise<void> {
  const login = getCurrentUserLogin()
  const queryClient = getQueryClient()
  const queryKey = issueViewerSecondaryViewQueryKey(owner, repo, number)
  const existingQuery = queryClient.getQueryCache().find({queryKey, exact: true})
  if (
    existingQuery &&
    (existingQuery.getObserversCount() > 0 ||
      existingQuery.state.fetchStatus !== 'idle' ||
      (existingQuery.state.status === 'success' && !existingQuery.state.isInvalidated))
  ) {
    return
  }

  // Keep pending preheats private so the viewer can fetch independently and mark the issue as read.
  const preheatClient = (preheatQueryClient ??= createQueryClient())
  await runSharedPreheat({
    client: preheatClient,
    queryKey,
    signal,
    onError,
    fetch: () =>
      fetchAndWarmPreheat({
        client: preheatClient,
        sharedClient: queryClient,
        queryOptions: issueViewerSecondaryViewQueryOptions({
          owner,
          repo,
          number,
          markAsRead: true,
          sendAnalyticsEvent,
        }),
        fetch: () =>
          preheatClient.fetchQuery({
            ...issueViewerSecondaryViewQueryOptions({
              owner,
              repo,
              number,
              markAsRead: false,
              sendAnalyticsEvent,
              preheat: true,
              preheatSource: source,
            }),
            gcTime: 0,
          }),
        canPublish: () =>
          isLoggedIn() && getCurrentUserLogin() === login && !isFeatureEnabled('disable_preheating_for_issues'),
      }),
  })
}
